import { useCallback } from 'react';

export interface UsePanelResizeOptions {
  minWidth: number;
  getMaxWidth: () => number;
  onWidthChange: (width: number) => void;
  rightMargin?: number;
  panelRef?: React.RefObject<HTMLElement | null>;
}

export function usePanelResize({
  minWidth,
  getMaxWidth,
  onWidthChange,
  rightMargin = 0,
  panelRef
}: UsePanelResizeOptions): (e: React.MouseEvent | React.PointerEvent) => void {
  return useCallback(
    (e: React.MouseEvent | React.PointerEvent) => {
      e.preventDefault();
      const target = e.currentTarget;
      const pointerId =
        e.nativeEvent instanceof PointerEvent ? e.nativeEvent.pointerId : 0;

      const useDirectDOM = Boolean(panelRef?.current);
      let lastWidth =
        useDirectDOM && panelRef?.current
          ? parseInt(panelRef.current.style.width, 10) || 0
          : 0;

      const onPointerMove = (ev: Event) => {
        const moveEvent = ev as PointerEvent;
        const rightEdge = document.documentElement.clientWidth - rightMargin;
        const rawWidth = Math.max(0, rightEdge - moveEvent.clientX);
        const clamped = Math.max(
          minWidth,
          Math.min(getMaxWidth(), rawWidth)
        );
        lastWidth = clamped;

        if (useDirectDOM && panelRef?.current) {
          panelRef.current.style.width = `${clamped}px`;
        } else {
          onWidthChange(clamped);
        }
      };

      const stopResize = () => {
        if (useDirectDOM) {
          onWidthChange(lastWidth);
        }
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.body.onselectstart = null;
        target.removeEventListener('pointermove', onPointerMove);
        target.removeEventListener('pointerup', stopResize);
        target.removeEventListener('pointercancel', stopResize);
        try {
          target.releasePointerCapture(pointerId);
        } catch {}
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.body.onselectstart = () => false;
      try {
        target.setPointerCapture(pointerId);
      } catch {}
      target.addEventListener('pointermove', onPointerMove);
      target.addEventListener('pointerup', stopResize);
      target.addEventListener('pointercancel', stopResize);
    },
    [minWidth, getMaxWidth, onWidthChange, rightMargin, panelRef]
  );
}
